import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { submitDelete, ENTITY_TYPE_LABELS } from '@/lib/deleteFlow';
import { extractError } from '@/lib/errors';

/**
 * Reusable confirmation dialog for deletes.
 *
 * Behaviour:
 *   - Super Admin: shows a simple "Confirm delete" prompt (no reason required); calls backend immediately.
 *   - Company Admin / User: forces a reason field; submits as a delete-request that flows through approval.
 *
 * Props:
 *   - open / onOpenChange
 *   - entity_type (string, key in ENTITY_TYPE_LABELS)
 *   - entity_id (string)
 *   - entity_label (string)
 *   - executor_kwargs (object, optional for nested ops like images / requirements)
 *   - company_id (string, optional override)
 *   - onSuccess (fn) - called after success with the backend result
 */
export default function DeleteConfirmDialog({
  open, onOpenChange,
  entity_type, entity_id, entity_label, executor_kwargs, company_id,
  onSuccess,
}) {
  const { isSuperAdmin } = useAuth();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const typeLabel = ENTITY_TYPE_LABELS[entity_type] || entity_type;
  const reasonRequired = !isSuperAdmin;

  const reset = () => { setReason(''); setSubmitting(false); };

  const handle = async () => {
    if (reasonRequired && !reason.trim()) {
      toast.error('Please provide a reason for deletion.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitDelete({
        entity_type, entity_id, entity_label,
        reason: reason.trim(),
        executor_kwargs, company_id,
      });
      if (res.deleted) {
        toast.success(`${typeLabel} deleted`);
      } else if (res.pending) {
        toast.success('Delete request submitted for approval');
      }
      onOpenChange(false);
      reset();
      if (onSuccess) onSuccess(res);
    } catch (e) {
      toast.error(extractError(e, 'Delete failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSuperAdmin ? (
              <ShieldCheck size={18} className="text-emerald-700" />
            ) : (
              <AlertTriangle size={18} className="text-amber-600" />
            )}
            {isSuperAdmin ? `Delete ${typeLabel}?` : `Request deletion of ${typeLabel}?`}
          </DialogTitle>
          <DialogDescription>
            {isSuperAdmin
              ? `This will permanently remove the ${typeLabel}. This action cannot be undone.`
              : `Your request will be reviewed by your Company Admin and then by a Super Admin before the ${typeLabel} is removed.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 text-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Item</div>
            <div className="font-medium text-[var(--ink-navy)]" data-testid="delete-dialog-entity-label">{entity_label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{typeLabel.replace('_', ' ')}</div>
          </div>

          {!isSuperAdmin && (
            <div>
              <Label htmlFor="delete-reason">Reason <span className="text-rose-600">*</span></Label>
              <Textarea
                id="delete-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why does this item need to be deleted? (mandatory)"
                rows={3}
                data-testid="delete-dialog-reason-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                You will be notified when your Company Admin or Super Admin acts on this request.
              </div>
            </div>
          )}

          {isSuperAdmin && (
            <div>
              <Label htmlFor="delete-reason-sa">Note (optional)</Label>
              <Textarea
                id="delete-reason-sa"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional reason for audit log"
                rows={2}
                data-testid="delete-dialog-reason-input"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} disabled={submitting} data-testid="delete-dialog-cancel">Cancel</Button>
          <Button
            onClick={handle}
            disabled={submitting || (reasonRequired && !reason.trim())}
            className={isSuperAdmin ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'luxury-button'}
            data-testid="delete-dialog-submit"
          >
            {submitting ? 'Submitting…' : (isSuperAdmin ? 'Delete now' : 'Submit request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Light helper: derive a short label for the entity being deleted. */
export function buildDeleteLabel(entity_type, entity) {
  if (!entity) return '';
  switch (entity_type) {
    case 'vendor':
    case 'goldsmith':
    case 'company':
      return entity.name || '';
    case 'gold_purity':
      return entity.name || '';
    case 'gold_rate':
      return `Rate ₹${entity.rate_per_gram_999}/g`;
    case 'diamond_classification':
      return `${entity.schema_code}${entity.shape_name ? ` — ${entity.shape_name}` : ''}`;
    case 'diamond_packet':
      return entity.packet_no || '';
    case 'order':
      return entity.order_no || '';
    case 'order_image':
      return `${entity.order_no || ''} image #${(entity.idx ?? 0) + 1}`;
    case 'order_requirement':
      return `${entity.order_no || ''} req`;
    case 'user':
      return entity.email || entity.name || '';
    default:
      return entity.id || '';
  }
}
